import React, { useEffect, useState } from "react";
import { Modal, Spin, Divider } from "antd";
import { PieChart, Pie, Cell } from "recharts";
import styles from "./student-exam-details.module.css";
import { getEcdStudentDetails, getStudentDetails } from "../../../../../../services/reportCard";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import CustomButton from "../../../../../../common/custom-button/custom-button";

const StudentExamDetailsModal = ({
  visible,
  onClose,
  studentId,
  examId,
  publishData,
  selectedStudentForModalData,
  onClickViewReport,
  isECDProgram,
  selectedTerm
}) => {
  const [loading, setLoading] = useState(false);
  const [examDetails, setExamDetails] = useState(null);

  const fetchExamDetails = async () => {
    if (!studentId || (!isECDProgram && !examId) || (isECDProgram && !selectedTerm)) return;
    setLoading(true);
    try {
      const params = { student_id: studentId };
      let response;
      if (isECDProgram) {
        params.term_id = selectedTerm;
        response = await getEcdStudentDetails(params);
      } else {
        params.exam_id = examId;
        response = await getStudentDetails(params);
      }
      setExamDetails({
        subjects: response?.data?.data,
      });
    } catch (error) {
      console.error("Failed to fetch exam details", error);
      setExamDetails(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      setExamDetails(null);
      fetchExamDetails();
    }
  }, [visible]);

  const renderSubjectList = (subjects, label, statusType) => {
    if (statusType === "allocated") {
      return (
        <div className={styles.subjectGroup}>
          <div className={styles.subjectGroupHeader}>
            <span className={`${styles.statusDot} ${styles.allocated}`} />
            <span className={styles.subjectGroupLabel}>
              {label} <b>({subjects?.length.toString().padStart(2, "0")})</b>
            </span>
          </div>

          <ul className={styles.subjectList}>
            {subjects?.map((subj, index) => (
              <li key={index} className={styles.subjectItem}>
                <span>{subj?.paper ? `${subj?.name} - ${subj?.paper}` : subj?.name}</span>
                <span className={styles.allocatedMark}>{subj?.marks}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    // Fallback for not_allocated and absent cases
    return (
      <div className={styles.subjectGroup}>
        <div className={styles.subjectGroupHeader}>
          <span
            className={`${styles.statusDot} ${
              statusType === "not_allocated"
                ? styles.notAllocated
                : styles.absent
            }`}
          />
          <span className={styles.subjectGroupLabel}>
            {label} <b>({subjects?.length.toString().padStart(2, "0")})</b>
          </span>
        </div>

        <ul className={styles.subjectList}>
          {subjects.map((subj, index) => (
            <li key={index} className={styles.subjectItem}>
              <span>{subj?.paper ? `${subj?.name} - ${subj?.paper}` : subj?.name}</span>
              <span className={styles.markText}>
                {statusType === "not_allocated" ? (
                  <span style={{ color: "#faad14" }}>N/A</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderChart = () => {
    const subjects = examDetails?.subjects || [];
    const totalSubjects = subjects.length;

    const allocated = subjects?.filter((s) =>
      isECDProgram
        ? s?.performance_comment && s?.behaviour_rating && s?.skills_development
        : s?.marks !== null && s?.marks !== "N/A"
    )?.length;

    const notApplicable = subjects?.filter((s) => s?.is_not_applicable)?.length;

    const notAllocated = subjects?.length - allocated - notApplicable;

    const chartData = [
      { name: "Allocated", value: allocated, color: "#39b54a" },
      { name: "Not Allocated", value: notAllocated, color: "#dda713" },
      { name: "Not Applicable", value: notApplicable, color: "#e05a21" },
    ].filter(d => d.value > 0);

    return (
      <div className={styles.pieWrapper}>
        <PieChart width={250} height={250}>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={70}
            outerRadius={100}
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={index} fill={entry?.color} />
            ))}
          </Pie>
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="middle"
            className={styles.pieCenterText}
          >
            <tspan
              x="50%"
              dy="-0.6em"
              style={{
                fill: "#5B626C",
                fontSize: "13.014px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "23.425px",
              }}
            >
              Total Subjects
            </tspan>
            <tspan
              x="50%"
              dy="1.2em"
              style={{
                fill: "#171717",
                textAlign: "center",
                fontSize: "18.219px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "23.425px",
              }}
            >
              {totalSubjects}
            </tspan>
          </text>
        </PieChart>
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={600}
      title={
        <>
          <div className={styles.modalTitle}>Examination Details</div>
          <Divider className={styles.modalTitleDivider} />
        </>
      }
    >
      <Spin spinning={loading}>
        {examDetails && (
          <>
            <div className={styles.contentWrapper}>
              {renderChart()}

              <div className={styles.detailsWrapper}>
                {isECDProgram ? (
                  <div className={styles.detailsWrapper}>
                    {renderSubjectList(
                      examDetails.subjects.filter(
                        (subj) => subj?.performance_comment && subj?.behaviour_rating && subj?.skills_development
                      ),
                      "Comment Assigned Across Subjects",
                      "allocated"
                    )}

                    {renderSubjectList(
                      examDetails.subjects.filter(
                        (subj) => (!subj?.performance_comment || !subj?.behaviour_rating || !subj?.skills_development) && !subj?.is_not_applicable
                      ),
                      "Comment Not Assigned",
                      "not_allocated"
                    )}
                  </div>
                ) : (
                  <div className={styles.detailsWrapper}>
                    {renderSubjectList(
                      examDetails.subjects.filter(
                        (subj) => subj?.marks !== null && subj?.marks !== "N/A"
                      ),
                      "Marks Allocation Across Subjects",
                      "allocated"
                    )}

                    {renderSubjectList(
                      examDetails.subjects.filter(
                        (subj) => (subj?.marks === null || subj?.marks === "N/A") && !subj?.is_not_applicable
                      ),
                      "Marks Not Allocated",
                      "not_allocated"
                    )}
                  </div>
                )}

                {renderSubjectList(
                  examDetails.subjects.filter(
                    (subj) => subj?.is_not_applicable
                  ),
                  "Subjects Not Applicable",
                  "not_applicable"
                )}
              </div>
            </div>

            <Divider />

            {selectedStudentForModalData?.is_published ? (
              <div className={styles.modalActions}>
                <CustomButton
                  title="View Report Card"
                  onClick={onClickViewReport}
                />
              </div>
            ) : (
              <>
                <div className={styles.modalActions}>
                  <CustomButton
                    title="Publish Report"
                    disabled={selectedStudentForModalData?.totalExams !== selectedStudentForModalData?.givenExams}
                    onClick={publishData}
                  />
                </div>
                <div className={styles.noteText}>
                  <span className={styles.warningRow}>
                    <span className={styles.warningIcon}>
                      <ExclamationCircleOutlined />
                    </span>
                    <span>
                      {`Please ensure all subject ${isECDProgram ? 'comments' : 'marks'
                        } are  allocated before publishing the report card.`}
                    </span>
                  </span>
                </div>
              </>
            )}
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default StudentExamDetailsModal;
